import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Alert } from 'antd';
import { AppBarCMS } from '../../../../basic';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import { CollapseBlock, MainTitle, Spinner } from '../../../../common';
import TableFilter from '../../../../common/TableFilter/TableFilter.jsx';
import { routes } from '../../../../../utils/helpers/ApiClient';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import useIsUserPhdAdmin from '../../../../../hooks/useIsUserPhdAdmin.jsx';
import checkPermission from '../../../../../utils/helpers/checkPermission.jsx';
import {
  useDeletePhdDissertationDefenseExaminerProposalMutation,
  useDeletePhdDissertationExamExaminerProposalMutation,
  useGetPhdDissertationDefenseExaminerProposalsQuery,
  useGetPhdDissertationExamExaminerProposalsQuery,
} from '../../../../../redux/services/phd';
import PhdDissertationExamExaminerProposalsTable from '../PhdDissertationExamExaminerProposals/PhdDissertationExamExaminerProposalsTable.jsx';
import PhdDissertationDefenseExaminerProposalsTable from '../PhdDissertationDefenseExaminerProposals/PhdDissertationDefenseExaminerProposalsTable.jsx';
import PhdArchiveFilter, { usePhdArchiveStatus } from '../PhdArchiveFilter.jsx';
import { getPhdApiErrorMessage } from '../utils/phdApiFeedback.js';

const FILTER_STORAGE_KEY = 'phd-proposals-filter';

const PhdExaminerProposalsPage = ({ initialTab }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const isAdmin = useIsUserPhdAdmin();
  const { deleteSuccess, deleteFailed } = useToastMessages();
  const { archiveStatus, setArchiveStatus } = usePhdArchiveStatus(
    'phd-examiner-proposals-archive-status',
  );
  const canViewExamProposals = checkPermission(
    user,
    'phd-dissertation-exam-examiner-proposals.index',
    isAdmin,
  );
  const canViewDefenseProposals = checkPermission(
    user,
    'phd-dissertation-defense-examiner-proposals.index',
    isAdmin,
  );
  const storedTab =
    typeof window !== 'undefined'
      ? window.localStorage.getItem(FILTER_STORAGE_KEY)
      : null;
  const [activeTab, setActiveTab] = useState(() => {
    const candidate = initialTab || storedTab;

    if (candidate === 'exam' && canViewExamProposals) {
      return candidate;
    }

    if (candidate === 'defense' && canViewDefenseProposals) {
      return candidate;
    }

    return canViewExamProposals
      ? 'exam'
      : canViewDefenseProposals
        ? 'defense'
        : null;
  });
  const [pagination, setPagination] = useState({ page: 1, per_page: 25 });
  const queryParams = {
    archive_status: archiveStatus,
    paginated: 1,
    page: pagination.page,
    per_page: pagination.per_page,
  };
  const {
    data: examProposalsResponse,
    isLoading: isLoadingExamProposals,
    error: examProposalsError,
    refetch: refetchExamProposals,
  } = useGetPhdDissertationExamExaminerProposalsQuery(
    queryParams,
    {
      skip: !canViewExamProposals || activeTab !== 'exam',
    },
  );
  const {
    data: defenseProposalsResponse,
    isLoading: isLoadingDefenseProposals,
    error: defenseProposalsError,
    refetch: refetchDefenseProposals,
  } = useGetPhdDissertationDefenseExaminerProposalsQuery(
    queryParams,
    {
      skip: !canViewDefenseProposals || activeTab !== 'defense',
    },
  );

  const examProposals = examProposalsResponse?.data;
  const defenseProposals = defenseProposalsResponse?.data;

  const [deleteExamProposal] =
    useDeletePhdDissertationExamExaminerProposalMutation();
  const [deleteDefenseProposal] =
    useDeletePhdDissertationDefenseExaminerProposalMutation();

  const tabDefinitions = useMemo(
    () => [
      {
        value: 'exam',
        label: t('Návrhy oponentov dizertačnej skúšky'),
        canView: canViewExamProposals,
        data: examProposals,
        pagination: examProposalsResponse,
        isLoading: isLoadingExamProposals,
        refetch: refetchExamProposals,
        deleteMutation: deleteExamProposal,
        createText: t('Pridať návrh oponenta'),
        createUrl: routes.cmsPhdDissertationExamExaminerProposalsCreate,
        editUrl: routes.cmsPhdDissertationExamExaminerProposalsEdit,
        TableComponent: PhdDissertationExamExaminerProposalsTable,
        dataPropName: 'dissertationExamExaminerProposals',
        permissionsBasic: 'phd-dissertation-exam-examiner-proposals',
      },
      {
        value: 'defense',
        label: t('Návrhy oponentov dizertačnej obhajoby'),
        canView: canViewDefenseProposals,
        data: defenseProposals,
        pagination: defenseProposalsResponse,
        isLoading: isLoadingDefenseProposals,
        refetch: refetchDefenseProposals,
        deleteMutation: deleteDefenseProposal,
        createText: t('Pridať návrh oponenta obhajoby'),
        createUrl: routes.cmsPhdDissertationDefenseExaminerProposalsCreate,
        editUrl: routes.cmsPhdDissertationDefenseExaminerProposalsEdit,
        TableComponent: PhdDissertationDefenseExaminerProposalsTable,
        dataPropName: 'dissertationDefenseExaminerProposals',
        permissionsBasic: 'phd-dissertation-defense-examiner-proposals',
      },
    ],
    [
      canViewDefenseProposals,
      canViewExamProposals,
      deleteDefenseProposal,
      deleteExamProposal,
      defenseProposals,
      defenseProposalsResponse,
      examProposals,
      examProposalsResponse,
      isLoadingDefenseProposals,
      isLoadingExamProposals,
      refetchDefenseProposals,
      refetchExamProposals,
      t,
    ],
  );

  const visibleTabs = useMemo(
    () => tabDefinitions.filter((tab) => tab.canView),
    [tabDefinitions],
  );

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.value === activeTab)) {
      setActiveTab(visibleTabs[0]?.value || null);
    }
  }, [activeTab, visibleTabs]);

  const activeConfig =
    visibleTabs.find((tab) => tab.value === activeTab) || visibleTabs[0];
  const pageTitle = t('Návrhy oponentov');

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  const handleDelete = async (row) => {
    if (!activeConfig?.deleteMutation) {
      return;
    }
    try {
      await activeConfig.deleteMutation(row.id).unwrap();
      deleteSuccess();
      activeConfig.refetch?.();
    } catch (error) {
      deleteFailed(error);
    }
  };

  const handleTabChange = (value) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(FILTER_STORAGE_KEY, value);
    }
    setPagination((current) => ({ ...current, page: 1 }));
    setActiveTab(value);
  };

  const handleArchiveStatusChange = (value) => {
    setPagination((current) => ({ ...current, page: 1 }));
    setArchiveStatus(value);
  };

  const handlePaginationChange = (nextPagination) => {
    setPagination({
      page: nextPagination.current,
      per_page: nextPagination.pageSize,
    });
  };

  if (!visibleTabs.length) {
    return (
      <>
        <AppBarCMS
          title={pageTitle}
          backUrl={routes.cmsDashboard}
          backUrlTooltip={t('Nástenka')}
        />
        <div style={{ margin: '10px' }}>
          <PhdPageContext
            title={t('Ako fungujú návrhy komisií?')}
            description={t(
              'Administrátor doktorandského štúdia vytvára návrh až z odovzdanej prihlášky na dizertačnú skúšku alebo obhajobu. Každá prihláška môže mať najviac jeden návrh.',
            )}
          />
          <CollapseBlock>
            <MainTitle title={pageTitle} />
            <p>{t('Nemáte oprávnenie na zobrazenie návrhov oponentov.')}</p>
          </CollapseBlock>
        </div>
      </>
    );
  }

  const TableComponent = activeConfig?.TableComponent;
  const tableProps = {
    onDetailClick: (row) => navigate(`${activeConfig.editUrl}/${row.id}`),
    onDelete: handleDelete,
  };
  if (activeConfig?.dataPropName) {
    tableProps[activeConfig.dataPropName] = activeConfig.data || [];
  }
  tableProps.pagination = {
    current: activeConfig?.pagination?.current_page ?? pagination.page,
    pageSize: activeConfig?.pagination?.per_page ?? pagination.per_page,
    total: activeConfig?.pagination?.total ?? 0,
    showSizeChanger: true,
    pageSizeOptions: [10, 25, 50, 100],
  };
  tableProps.onPaginationChange = handlePaginationChange;

  const loadError = examProposalsError || defenseProposalsError;

  if (loadError) {
    return (
      <Alert
        type="error"
        showIcon
        title={getPhdApiErrorMessage(
          loadError,
          t(
            'PhD údaje sa nepodarilo načítať. Obnovte stránku alebo skúste znova.',
          ),
        )}
      />
    );
  }

  return (
    <>
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsDashboard}
        backUrlTooltip={t('Nástenka')}
        createText={activeConfig?.createText}
        createUrl={activeConfig?.createUrl}
        permissionsBasic={activeConfig?.permissionsBasic}
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako fungujú návrhy komisií?')}
          description={t(
            'Vyberte typ návrhu a otvorte existujúci záznam alebo vytvorte nový z odovzdanej nadradenej prihlášky.',
          )}
        />
        <PhdArchiveFilter
          archiveStatus={archiveStatus}
          onChange={handleArchiveStatusChange}
          storageKey="phd-examiner-proposals-archive-status"
        />
        <CollapseBlock>
          <TableFilter
            title={t('Filter návrhov')}
            storageKey={FILTER_STORAGE_KEY}
            value={activeConfig?.value}
            setValue={handleTabChange}
            options={visibleTabs.map((tab) => ({
              value: tab.value,
              label: tab.label,
            }))}
          />
        </CollapseBlock>

        <CollapseBlock>
          <MainTitle title={activeConfig?.label || pageTitle} />
          {activeConfig?.isLoading ? (
            <Spinner />
          ) : (
            TableComponent && <TableComponent {...tableProps} />
          )}
        </CollapseBlock>
      </div>
    </>
  );
};

PhdExaminerProposalsPage.propTypes = {
  initialTab: PropTypes.string,
};

export default PhdExaminerProposalsPage;
